<template>
  <v-app>
    <!-- ===================== MODAL ===================== -->
    <v-dialog v-model="localDialog" max-width="800px" persistent>
      <v-card>
        <v-card-title class="text-h6 bg-blue-grey-lighten-4">
          <span>{{ isEntryMode ? "Add Production Style" : "Update Production Style" }}</span>
        </v-card-title>

        <v-card-text>
          <v-form @submit.prevent="submit">
            <v-row>

              <!-- Buyer -->
              <v-col cols="12" md="6">
                <v-select
                  v-model="buyer_id.value.value"
                  :items="customerList"
                  :item-title="item => `${item.customer?.company_name ?? ''} - ${item.customer?.code ?? ''}`"
                  :item-value="item => item.customer?.id ?? null"
                  variant="outlined"
                  density="compact"
                  :error-messages="buyer_id.errorMessage.value"
                  clearable
                >
                  <template #label>
                    Buyer <span class="text-red">*</span>
                  </template>
                </v-select>
              </v-col>

              <!-- Name -->
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="name.value.value"
                  variant="outlined"
                  density="compact"
                  :error-messages="name.errorMessage.value"
                >
                  <template #label>
                    Name <span class="text-red">*</span>
                  </template>
                </v-text-field>
              </v-col>

              <!-- Product Type -->
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="product_type.value.value"
                  variant="outlined"
                  density="compact"
                  :error-messages="product_type.errorMessage.value"
                >
                  <template #label>
                    Product Type <span class="text-red">*</span>
                  </template>
                </v-text-field>
              </v-col>

              <!-- Fabric Type -->
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="fabric_type.value.value"
                  variant="outlined"
                  density="compact"
                  :error-messages="fabric_type.errorMessage.value"
                  label="Fabric Type"
                />
              </v-col>

              <!-- Composition -->
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="composition.value.value"
                  variant="outlined"
                  density="compact"
                  :error-messages="composition.errorMessage.value"
                  label="Composition"
                />
              </v-col>

              <!-- GSM -->
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="gsm.value.value"
                  type="number"
                  variant="outlined"
                  density="compact"
                  :error-messages="gsm.errorMessage.value"
                  label="GSM"
                />
              </v-col>

              <!-- Front Image -->
              <v-col cols="12" md="6">
                <v-file-input
                  label="Front Image"
                  variant="outlined"
                  density="compact"
                  accept="image/*"
                  prepend-icon=""
                  @change="e => image_front = e.target.files[0]"
                  clearable
                />
              </v-col>

              <!-- Back Image -->
              <v-col cols="12" md="6">
                <v-file-input
                  label="Back Image"
                  variant="outlined"
                  density="compact"
                  accept="image/*"
                  prepend-icon=""
                  @change="e => image_back = e.target.files[0]"
                  clearable
                />
              </v-col>

              <!-- Description -->
              <v-col cols="12" md="6">
                <v-textarea
                  v-model="description.value.value"
                  variant="outlined"
                  density="compact"
                  rows="2"
                  auto-grow
                  :error-messages="description.errorMessage.value"
                  label="Description"
                />
              </v-col>

              <!-- Status -->
              <v-col cols="12" md="6">
                <v-checkbox
                  v-model="status.value.value"
                  :true-value="1"
                  :false-value="0"
                  label="Active"
                  color="primary"
                />
              </v-col>

              <input type="hidden" v-model="style_id.value.value" />

            </v-row>

            <v-divider class="my-3" />

            <v-card-actions class="justify-end">
              <v-btn color="red" variant="flat" @click="closeDialog">Close</v-btn>
              <v-btn color="primary" variant="flat" type="submit">
                {{ isEntryMode ? "Save" : "Update" }}
              </v-btn>
            </v-card-actions>
          </v-form>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- ===================== LIST ===================== -->
    <v-container fluid>
      <v-row>
        <v-col cols="12">
          <v-expansion-panels v-model="panel">
            <v-expansion-panel>
              <v-expansion-panel-title class="bg-blue-grey-lighten-4">
                Production Style List
              </v-expansion-panel-title>

              <v-expansion-panel-text>

                <v-row class="py-2">
                  <v-col md="4">
                    <v-text-field
                      v-model="search"
                      label="Search"
                      density="compact"
                      variant="outlined"
                      append-inner-icon="mdi-magnify"
                      clearable
                    />
                  </v-col>

                  <v-col md="3" offset-md="5" class="text-right">
                    <v-btn color="primary" prepend-icon="mdi-plus" @click="openCreate">
                      Add Production Style
                    </v-btn>
                  </v-col>
                </v-row>

                <v-data-table
                  :headers="headers"
                  :items="productionStyles"
                  :search="search"
                  density="compact"
                  :loading="loading"
                >
                  <template #item.index="{ index }">
                    {{ index + 1 }}
                  </template>

                  <template #item.image_front="{ item }">
                      <v-img style="height:30px; width:30px;" v-if="item.image_front" :src="fileServerUrl + item.image_front" cover />

                      <v-icon v-else color="grey">
                        mdi-image-off-outline
                      </v-icon>
                  </template>


                  <template #item.image_back="{ item }">
                      <v-img style="height:30px; width:30px;" v-if="item.image_back" :src="fileServerUrl + item.image_back" cover />

                    <v-icon v-else color="grey">
                      mdi-image-off-outline
                    </v-icon>
                  </template>

                  <template #item.buyer="{ item }">
                    {{ item.buyer?.company_name }} - {{ item.buyer?.code }}
                  </template>

                  <template #item.status="{ item }">
                    <v-chip size="small" :color="item.status == 1 ? 'success' : 'red'">
                      {{ item.status == 1 ? "Active" : "Inactive" }}
                    </v-chip>
                  </template>

                  <template #item.actions="{ item }">
                    <v-icon color="primary" @click="openEdit(item)">
                      mdi-pencil-outline
                    </v-icon>
                  </template>
                </v-data-table>

              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>
    </v-container>
  </v-app>
</template>

<script>
import { ref } from "vue";
import { useForm, useField } from "vee-validate";
import * as yup from "yup";
import { useStore } from "vuex";
import { toast } from "vue3-toastify";
import { fileServerUrl } from "@/store/api";

export default {
  data() {
    return {
      fileServerUrl,
      panel: [0],
      localDialog: false,
      loading: false,
      search: "",
      isEntryMode: true,
      headers: [
        { title: "#", key: "index", align: "center" },
        { title: "Code", key: "code" },
        { title: "Style Name", key: "name" },
        { title: "Front Image", key: "image_front", align: "center", sortable: false },
        { title: "Back Image", key: "image_back", align: "center", sortable: false },
        { title: "Product Type", key: "product_type" },
        { title: "Buyer", key: "buyer" },
        { title: "Fabric", key: "fabric_type" },
        { title: "GSM", key: "gsm", align: "center" },
        { title: "Status", key: "status", align: "center" },
        { title: "Actions", key: "actions", align: "center", sortable: false },
      ],
    };
  },

  setup() {
    const store = useStore();
    const image_front = ref(null);
    const image_back = ref(null);
    const localDialog = ref(false);

    const { handleSubmit, resetForm } = useForm({
      validationSchema: yup.object({
        buyer_id: yup.number().required(),
        name: yup.string().required().max(255),
        product_type: yup.string().required().max(255),
        fabric_type: yup.string().nullable(),
        composition: yup.string().nullable(),
        gsm: yup.number().nullable(),
        description: yup.string().nullable(),
        status: yup.number().required(),
      }),
    });

    const buyer_id = useField("buyer_id");
    const name = useField("name");
    const product_type = useField("product_type");
    const fabric_type = useField("fabric_type");
    const composition = useField("composition");
    const gsm = useField("gsm");
    const description = useField("description");
    const status = useField("status");
    const style_id = useField("style_id");

    const submit = handleSubmit(values => {
      const fd = new FormData();
      const payload = {};
      
      if(!values.style_id){
        Object.keys(values).forEach(key => {
          if (values[key] !== null && values[key] !== undefined) {
            fd.append(key, values[key]);
          }
        });

        if (image_front.value) fd.append("image_front", image_front.value);
        if (image_back.value) fd.append("image_back", image_back.value);
     }


     
     if(values.style_id){
        Object.keys(values).forEach(key => {
          if (values[key] !== null && values[key] !== undefined) {
            if (typeof values[key] === "boolean") {
              payload[key] = values[key] ? 1 : 0;
            } else {
              payload[key] = values[key];
            }
          }
        });
        if (image_front.value) payload.image_front = image_front.value;
        if (image_back.value) payload.image_back = image_back.value;
      }

      const action = values.style_id
        ? store.dispatch("updateProductionStyle", { id: values.style_id, data: payload })
        : store.dispatch("saveProductionStyle", fd);

      action.then(() => {
        toast.success("Success!");
        resetForm();
        closeDialog();
        store.dispatch("getProductionStyleList");
      });
    });

    const closeDialog = () => {
      localDialog.value = false;
      resetForm();
    };

    return {
      buyer_id,
      name,
      product_type,
      fabric_type,
      composition,
      gsm,
      description,
      status,
      style_id,
      image_front,
      image_back,
      submit,
      resetForm,
      localDialog,
    };
  },

  computed: {
    productionStyles() {
      return this.$store.getters.getProductionStyleList;
    },
    customerList() {
      return this.$store.getters.getCustomerMapList;
    },
  },

  methods: {
    loadList() {
      this.loading = true;
      this.$store.dispatch("getProductionStyleList").finally(() => {
        this.loading = false;
      });
      this.$store.dispatch("getCustomerMapList");
    },

    openCreate() {
      this.isEntryMode = true;
      this.localDialog = true;
      this.resetForm();
      this.status.setValue(1);
    },

    openEdit(item) {
      this.isEntryMode = false;
      this.localDialog = true;
      console.log(item)
      this.style_id.setValue(item.id);
      this.buyer_id.setValue(item.buyer_id);
      this.name.setValue(item.name);
      this.product_type.setValue(item.product_type);
      this.fabric_type.setValue(item.fabric_type);
      this.composition.setValue(item.composition);
      this.gsm.setValue(item.gsm);
      this.description.setValue(item.description);
      this.status.setValue(item.status);
      this.image_front.setValue(item.image_front);
      this.image_back.setValue(item.image_back);
    },

    closeDialog() {
      this.localDialog = false;
      this.resetForm();
    },
  },

  created() {
    this.loadList();
  },
};
</script>
